import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import VerifiedBadge, { isVerified } from "../components/VerifiedBadge";
import { useAuth } from "../context/AuthContext";
import { Heart, Send, MessageCircle, Sparkles, Crown, Lock, X, Eye, Users, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDER = "https://images.pexels.com/photos/8629727/pexels-photo-8629727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Activity = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "matches");
  const [data, setData] = useState({ matches: [], myLikes: [], admirers: [] });
  const [loading, setLoading] = useState(true);
  const [admirersLocked, setAdmirersLocked] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [matchesRes, myLikesRes, statsRes] = await Promise.all([
        client.get("/matches"),
        client.get("/my-likes"),
        client.get("/stats").catch(() => null),
      ]);
      let admirers = [];
      try {
        const ad = await client.get("/admirers");
        admirers = ad.data.admirers;
      } catch (err) {
        if (err?.response?.status === 402) setAdmirersLocked(true);
      }
      setData({
        matches: matchesRes.data.matches || [],
        myLikes: myLikesRes.data.likes || [],
        admirers,
      });
      if (statsRes) setStats(statsRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const switchTab = (t) => {
    setTab(t);
    setParams({ tab: t });
  };

  const tabs = [
    { id: "matches", label: "Ligações", count: data.matches.length, icon: Heart },
    { id: "sent", label: "Convidei", count: data.myLikes.length, icon: Send },
    { id: "received", label: "Convidaram-me", count: admirersLocked ? "•" : data.admirers.length, icon: Sparkles, premium: !user?.is_premium },
  ];

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="activity-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <span className="label-uppercase">Actividade</span>
        <h1 className="font-display text-3xl md:text-5xl mt-3 mb-2">A tua dinâmica</h1>
        <p className="text-[#A29F9B] mb-8">Quem convidaste, quem te convidou e as ligações mútuas.</p>

        {/* KPIs (formerly /dashboard "Painel") */}
        {stats && (
          <section className="mb-10" data-testid="activity-kpis">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-[#C5A880]" />
              <span className="label-uppercase">Painel</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Stat icon={Send}      label="Convites enviados"  value={stats.likes_sent}             testid="stat-sent" />
              <Stat icon={Eye}       label="Convidaram-me"      value={stats.pending_admirers ?? stats.likes_received}  testid="stat-received" />
              <Stat icon={Sparkles}  label="Ligações mútuas"    value={stats.matches}                testid="stat-matches" />
              <Stat icon={Users}     label="Candidatos compatíveis" value={stats.compatible_candidates ?? stats.active_candidates} testid="stat-active" />
            </div>
          </section>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/5 mb-10 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0 scrollbar-hide">
          {tabs.map((t) => (
            <button key={t.id} onClick={()=>switchTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-3 sm:px-5 py-3 inline-flex items-center gap-2 text-sm transition-colors border-b-2 -mb-px shrink-0 whitespace-nowrap ${
                tab === t.id ? "border-[#C5A880] text-[#C5A880]" : "border-transparent text-[#A29F9B] hover:text-[#DCD7D2]"
              }`}>
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.premium && <Lock className="w-3 h-3 ml-1" />}
              <span className={`text-xs px-2 py-0.5 rounded-full ${tab === t.id ? "bg-[#C5A880]/20" : "bg-white/5"}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {loading && <div className="text-[#A29F9B]">A carregar...</div>}

        {!loading && tab === "matches" && (
          <Grid items={data.matches.map(m => ({
            key: m.match_id, profile: m.profile, score: m.compatibility.overall,
            sub: m.last_message ? m.last_message.text : "Diz olá pela primeira vez.",
            cta: <span className="inline-flex items-center gap-1 text-xs text-[#C5A880]"><MessageCircle className="w-3 h-3" /> Abrir conversa</span>,
            link: `/profile/${m.profile.id}`,
            ctaLink: `/chat/${m.match_id}`,
          }))} emptyTitle="Sem ligações mútuas ainda" emptyText="Continua a explorar — quando alguém também te der like, aparece aqui." />
        )}

        {!loading && tab === "sent" && (
          <>
            {data.myLikes.length > 0 && user && (() => {
              const photoCount = (user.photos || []).length;
              const bioLen = (user.bio || "").length;
              const incomplete = photoCount < 3 || bioLen < 200;
              if (!incomplete) return null;
              return (
                <div className="bg-[#C5A880]/10 border border-[#C5A880]/30 rounded-2xl p-5 mb-6 flex items-start gap-3" data-testid="convidei-tip">
                  <Sparkles className="w-5 h-5 text-[#C5A880] mt-0.5 shrink-0" />
                  <div className="text-sm text-[#DCD7D2] leading-relaxed flex-1">
                    <p className="font-medium text-[#F5F2EE] mb-1">Sobe as tuas hipóteses de receberes resposta</p>
                    <p>
                      Perfis com {photoCount < 3 && <strong>3+ fotografias</strong>}
                      {photoCount < 3 && bioLen < 200 && " e "}
                      {bioLen < 200 && <strong>uma bio cuidada (200+ caracteres)</strong>}
                      {" "}recebem muito mais respostas. Ainda recebes <strong>+1 ficha de descoberta</strong> ao completares.
                    </p>
                    <Link to="/perfil" className="inline-flex items-center gap-1 mt-2 text-[#C5A880] hover:underline text-xs"
                      data-testid="convidei-tip-cta">
                      Completar perfil →
                    </Link>
                  </div>
                </div>
              );
            })()}
            <Grid items={data.myLikes.map(m => ({
              key: m.profile.id, profile: m.profile, score: m.compatibility.overall,
              sub: "Aguardas que ele/ela responda.",
              cta: <span className="inline-flex items-center gap-1 text-xs text-[#A29F9B]"><Send className="w-3 h-3" /> Convite enviado</span>,
              link: `/profile/${m.profile.id}`,
              extra: (
                <CancelInviteButton
                  profileId={m.profile.id}
                  profileName={m.profile.name}
                  onCancelled={() => setData(d => ({ ...d, myLikes: d.myLikes.filter(x => x.profile.id !== m.profile.id) }))}
                />
              ),
            }))} emptyTitle="Não enviaste convites ainda" emptyText="Visita Descobrir e demonstra interesse — ele/ela só sabe se também gostar." />
          </>
        )}

        {!loading && tab === "received" && admirersLocked && (
          <div className="bg-[#161920] border border-white/5 rounded-2xl p-12 text-center" data-testid="received-locked">
            <Crown className="w-10 h-10 text-[#C5A880] mx-auto mb-4" />
            <h3 className="font-display text-2xl mb-2">Quem te convidou é Premium</h3>
            <p className="text-[#A29F9B] mb-6">Subscreve para ver os perfis que demonstraram interesse por ti.</p>
            <Link to="/premium" className="btn-primary inline-block" data-testid="received-upgrade">Subscrever Premium</Link>
          </div>
        )}

        {!loading && tab === "received" && !admirersLocked && (
          <Grid items={data.admirers.map(a => ({
            key: a.profile.id, profile: a.profile, score: a.compatibility.overall,
            sub: `Demonstrou interesse em ${new Date(a.since).toLocaleDateString("pt-PT")}.`,
            cta: <span className="inline-flex items-center gap-1 text-xs text-[#C5A880]"><Heart className="w-3 h-3" /> Ver e responder</span>,
            link: `/profile/${a.profile.id}`,
          }))} emptyTitle="Ainda ninguém te convidou" emptyText="Mantém o teu perfil completo e o questionário actualizado para apareceres melhor." />
        )}
      </main>
      <Footer />
    </div>
  );
};

const Grid = ({ items, emptyTitle, emptyText }) => {
  if (items.length === 0) {
    return (
      <div className="bg-[#161920] border border-white/5 rounded-2xl p-12 text-center" data-testid="empty-grid">
        <Sparkles className="w-8 h-8 text-[#C5A880] mx-auto mb-4" />
        <h3 className="font-display text-2xl mb-2">{emptyTitle}</h3>
        <p className="text-[#A29F9B]">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map(({ key, profile, score, sub, cta, link, ctaLink, extra }) => {
        // The photo+name area always goes to the profile detail page.
        // The CTA at the bottom goes to a separate destination (chat/profile)
        // when `ctaLink` is provided — otherwise both share `link`.
        const photoLink = link;
        const action = ctaLink || link;
        return (
          <div key={key}
            className="group bg-[#161920] border border-white/5 rounded-2xl overflow-hidden hover:border-[#C5A880]/40 transition-all relative"
            data-testid={`activity-card-${key}`}>
            {extra && (
              <div className="absolute top-3 left-3 z-10">{extra}</div>
            )}
            <Link to={photoLink} className="block" data-testid={`activity-card-photo-${key}`}>
              <div className="aspect-[3/4] overflow-hidden relative">
                <img src={profile.photos?.[0] || PLACEHOLDER} alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {isVerified(profile) && (
                  <div className="absolute top-3 right-3"><VerifiedBadge size="sm" /></div>
                )}
              </div>
              <div className="px-5 pt-5 pb-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl">{profile.name}, {profile.age}</h3>
                  <span className="text-[#C5A880] font-display text-lg">{score}%</span>
                </div>
                <p className="text-sm text-[#A29F9B] mt-2 line-clamp-1">{sub}</p>
              </div>
            </Link>
            <Link to={action} className="block px-5 pb-5" data-testid={`activity-card-cta-${key}`}>
              {cta}
            </Link>
          </div>
        );
      })}
    </div>
  );
};

const CancelInviteButton = ({ profileId, profileName, onCancelled }) => {
  const [busy, setBusy] = useState(false);
  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    const ok = window.confirm(
      `Cancelar o convite a ${profileName}?\n\nO like é apagado em definitivo. Podes voltar a explorar este perfil mais tarde no Discover.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      await client.delete(`/like/${profileId}`);
      toast.success(`Convite a ${profileName} cancelado.`);
      onCancelled?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Falha ao cancelar o convite.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className="w-9 h-9 rounded-full bg-[#0C0E12]/80 backdrop-blur-md border border-white/10 hover:border-[#E37B7B] hover:text-[#E37B7B] text-[#DCD7D2] inline-flex items-center justify-center transition-colors disabled:opacity-50"
      title="Cancelar convite"
      aria-label={`Cancelar convite a ${profileName}`}
      data-testid={`cancel-invite-${profileId}`}
    >
      <X className="w-4 h-4" />
    </button>
  );
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-[#161920] border border-white/5 rounded-2xl p-4 sm:p-5" data-testid={testid}>
    <Icon className="w-4 h-4 text-[#C5A880]" />
    <div className="mt-3 font-display text-2xl sm:text-3xl text-[#F5F2EE]">{value ?? "—"}</div>
    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-[#A29F9B] mt-1.5 leading-snug">{label}</div>
  </div>
);

export default Activity;
